import { findKnownPlaceholders, findKnownPlaceholderTextRanges, isInlineTextFlowElement } from './placeholderHtml';

export const PLACEHOLDER_KEY_ATTRIBUTE = 'data-placeholder-key';
const PLACEHOLDER_SELECTOR = `[${PLACEHOLDER_KEY_ATTRIBUTE}]`;

type DomPoint = {
  node: Node;
  offset: number;
};

type ReplacementSegment = {
  start: number;
  end: number;
  textNode: Text;
};

type PlaceholderRegion = {
  start: DomPoint;
  end: DomPoint;
  unit: HTMLElement | null;
};

function createCollapsedRange(point: DomPoint): Range {
  const range = point.node.ownerDocument?.createRange() ?? document.createRange();
  range.setStart(point.node, point.offset);
  range.collapse(true);
  return range;
}

function comparePoints(left: DomPoint, right: DomPoint): number {
  return createCollapsedRange(left).compareBoundaryPoints(Range.START_TO_START, createCollapsedRange(right));
}

function rangeStart(range: Range): DomPoint {
  return { node: range.startContainer, offset: range.startOffset };
}

function rangeEnd(range: Range): DomPoint {
  return { node: range.endContainer, offset: range.endOffset };
}

function unitTextBoundaries(unit: HTMLElement): { start: DomPoint; end: DomPoint } {
  const walker = unit.ownerDocument.createTreeWalker(unit, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let current = walker.nextNode();
  while (current) {
    textNodes.push(current as Text);
    current = walker.nextNode();
  }

  if (textNodes.length === 0) {
    return {
      start: { node: unit, offset: 0 },
      end: { node: unit, offset: unit.childNodes.length },
    };
  }

  const last = textNodes[textNodes.length - 1];
  return {
    start: { node: textNodes[0], offset: 0 },
    end: { node: last, offset: last.data.length },
  };
}

function isRangeInsideEditor(editor: HTMLElement, range: Range): boolean {
  const startsInside = range.startContainer === editor || editor.contains(range.startContainer);
  const endsInside = range.endContainer === editor || editor.contains(range.endContainer);
  return startsInside && endsInside;
}

function getPlaceholderUnits(editor: HTMLElement): HTMLElement[] {
  return Array.from(editor.querySelectorAll<HTMLElement>(PLACEHOLDER_SELECTOR));
}

function getPlaceholderRegions(editor: HTMLElement, knownKeys: readonly string[]): PlaceholderRegion[] {
  const regions: PlaceholderRegion[] = getPlaceholderUnits(editor).map((unit) => ({
    ...unitTextBoundaries(unit),
    unit,
  }));

  if (knownKeys.length === 0) return regions;

  findKnownPlaceholderTextRanges(editor, knownKeys).forEach((range) => {
    if (!range.start.node.parentElement?.closest(PLACEHOLDER_SELECTOR)) {
      regions.push({ ...range, unit: null });
    }
  });

  return regions;
}

function overlapsRegion(range: Range, region: PlaceholderRegion): boolean {
  return comparePoints(rangeStart(range), region.end) < 0 && comparePoints(rangeEnd(range), region.start) > 0;
}

function fullyCoversRegion(range: Range, region: PlaceholderRegion): boolean {
  return comparePoints(rangeStart(range), region.start) <= 0 && comparePoints(rangeEnd(range), region.end) >= 0;
}

function pointWithinRegion(point: DomPoint, region: PlaceholderRegion): boolean {
  return comparePoints(point, region.start) > 0 && comparePoints(point, region.end) < 0;
}

function structuralBoundaryAliases(point: DomPoint, editor: HTMLElement): DomPoint[] {
  const aliases: DomPoint[] = [];
  const pending: DomPoint[] = [point];
  const seen = new Map<Node, Set<number>>();

  while (pending.length > 0) {
    const current = pending.shift();
    if (!current) break;
    const seenOffsets = seen.get(current.node) ?? new Set<number>();
    if (!seenOffsets.has(current.offset)) {
      seenOffsets.add(current.offset);
      seen.set(current.node, seenOffsets);
      aliases.push(current);

      const canAscend =
        current.node !== editor &&
        (current.node.nodeType !== Node.ELEMENT_NODE || isInlineTextFlowElement(current.node as Element));
      const parent = canAscend ? current.node.parentNode : null;
      if (parent && (parent === editor || editor.contains(parent))) {
        const index = Array.from(parent.childNodes).indexOf(current.node as ChildNode);
        if (index >= 0) {
          const length =
            current.node.nodeType === Node.TEXT_NODE
              ? (current.node as Text).data.length
              : current.node.childNodes.length;
          if (current.offset === 0) pending.push({ node: parent, offset: index });
          if (current.offset === length) pending.push({ node: parent, offset: index + 1 });
        }
      }
    }
  }

  return aliases;
}

function pointsShareStructuralBoundary(editor: HTMLElement, left: DomPoint, right: DomPoint): boolean {
  const rightAliases = structuralBoundaryAliases(right, editor);
  return structuralBoundaryAliases(left, editor).some((leftAlias) =>
    rightAliases.some((rightAlias) => leftAlias.node === rightAlias.node && leftAlias.offset === rightAlias.offset),
  );
}

function unwrap(element: HTMLElement): void {
  const parent = element.parentNode;
  if (!parent) return;

  const fragment = element.ownerDocument.createDocumentFragment();
  while (element.firstChild) fragment.appendChild(element.firstChild);
  parent.replaceChild(fragment, element);
}

export function createPlaceholderUnit(key: string, ownerDocument: Document = document): HTMLElement {
  const unit = ownerDocument.createElement('span');
  unit.setAttribute(PLACEHOLDER_KEY_ATTRIBUTE, key);
  unit.setAttribute('contenteditable', 'false');
  unit.setAttribute('spellcheck', 'false');
  unit.textContent = `{{${key}}}`;
  return unit;
}

/** Marks intact known tokens without joining legacy fragments across HTML nodes. */
export function markKnownPlaceholders(editor: HTMLElement, knownKeys: readonly string[]): void {
  if (knownKeys.length === 0) return;

  const selection = editor.ownerDocument.getSelection();
  const selectedRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
  const savedRange = selectedRange && isRangeInsideEditor(editor, selectedRange) ? selectedRange.cloneRange() : null;
  const originalStart = savedRange ? rangeStart(savedRange) : null;
  const originalEnd = savedRange ? rangeEnd(savedRange) : null;
  const replacements = new Map<Text, ReplacementSegment[]>();

  const walker = editor.ownerDocument.createTreeWalker(editor, NodeFilter.SHOW_TEXT, {
    acceptNode(node: Node): number {
      return (node.parentElement?.closest(PLACEHOLDER_SELECTOR) as HTMLElement | null)?.closest(PLACEHOLDER_SELECTOR)
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT;
    },
  });
  const textNodes: Text[] = [];
  let current = walker.nextNode();
  while (current) {
    textNodes.push(current as Text);
    current = walker.nextNode();
  }

  textNodes.forEach((textNode) => {
    const matches = findKnownPlaceholders(textNode.data, knownKeys);
    const parent = textNode.parentNode;
    if (matches.length === 0 || !parent) return;

    const fragment = editor.ownerDocument.createDocumentFragment();
    const segments: ReplacementSegment[] = [];
    let cursor = 0;

    matches.forEach((match) => {
      if (match.start > cursor) {
        const ordinaryText = editor.ownerDocument.createTextNode(textNode.data.slice(cursor, match.start));
        fragment.appendChild(ordinaryText);
        segments.push({ start: cursor, end: match.start, textNode: ordinaryText });
      }

      const unit = createPlaceholderUnit(match.key, editor.ownerDocument);
      const tokenText = unit.firstChild as Text;
      fragment.appendChild(unit);
      segments.push({ start: match.start, end: match.end, textNode: tokenText });
      cursor = match.end;
    });

    if (cursor < textNode.data.length) {
      const ordinaryText = editor.ownerDocument.createTextNode(textNode.data.slice(cursor));
      fragment.appendChild(ordinaryText);
      segments.push({ start: cursor, end: textNode.data.length, textNode: ordinaryText });
    }

    replacements.set(textNode, segments);
    parent.replaceChild(fragment, textNode);
  });

  if (!savedRange || !originalStart || !originalEnd || replacements.size === 0 || !selection) return;

  const remap = (point: DomPoint): DomPoint => {
    if (!(point.node instanceof Text)) return point;
    const segments = replacements.get(point.node);
    if (!segments) return point;

    const segment =
      segments.find((candidate) => point.offset >= candidate.start && point.offset <= candidate.end) ??
      segments[segments.length - 1];
    return {
      node: segment.textNode,
      offset: Math.max(0, Math.min(point.offset - segment.start, segment.textNode.data.length)),
    };
  };

  const newStart = remap(originalStart);
  const newEnd = remap(originalEnd);
  if (!newStart.node.isConnected || !newEnd.node.isConnected) return;

  const restored = editor.ownerDocument.createRange();
  restored.setStart(newStart.node, newStart.offset);
  restored.setEnd(newEnd.node, newEnd.offset);
  selection.removeAllRanges();
  selection.addRange(restored);
}

/** Relocks valid runtime units without structurally changing browser-authored text or its undo history. */
export function stabilizePlaceholderUnits(editor: HTMLElement, knownKeys: readonly string[]): void {
  const knownKeySet = new Set(knownKeys);
  getPlaceholderUnits(editor).forEach((unit) => {
    const key = unit.getAttribute(PLACEHOLDER_KEY_ATTRIBUTE) ?? '';
    if (!knownKeySet.has(key) || unit.textContent !== `{{${key}}}`) {
      unwrap(unit);
      return;
    }
    unit.setAttribute('contenteditable', 'false');
    unit.setAttribute('spellcheck', 'false');
  });
}

export function reconcilePlaceholderUnits(editor: HTMLElement, knownKeys: readonly string[]): void {
  stabilizePlaceholderUnits(editor, knownKeys);
  markKnownPlaceholders(editor, knownKeys);
}

export function setProtectedEditorHtml(editor: HTMLElement, html: string, knownKeys: readonly string[]): void {
  const target = editor;
  target.innerHTML = html || '';
  reconcilePlaceholderUnits(target, knownKeys);
}

/** Returns storage HTML without runtime marker wrappers or editing attributes. */
export function serializeProtectedEditorHtml(editor: HTMLElement): string {
  const clone = editor.cloneNode(true) as HTMLElement;
  getPlaceholderUnits(clone).forEach(unwrap);
  return clone.innerHTML;
}

export function getEditorSelectionRange(editor: HTMLElement): Range | null {
  const selection = editor.ownerDocument.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  return isRangeInsideEditor(editor, range) ? range : null;
}

export function rangeIntersectsPlaceholder(
  editor: HTMLElement,
  range: Range,
  knownKeys: readonly string[] = [],
): boolean {
  if (!isRangeInsideEditor(editor, range)) return false;
  return getPlaceholderRegions(editor, knownKeys).some((region) => overlapsRegion(range, region));
}

export function rangePartiallyIntersectsPlaceholder(
  editor: HTMLElement,
  range: Range,
  knownKeys: readonly string[] = [],
): boolean {
  if (!isRangeInsideEditor(editor, range)) return false;
  return getPlaceholderRegions(editor, knownKeys).some(
    (region) => overlapsRegion(range, region) && !fullyCoversRegion(range, region),
  );
}

export function selectionIsInsidePlaceholder(
  editor: HTMLElement,
  range: Range,
  knownKeys: readonly string[] = [],
): boolean {
  if (!range.collapsed || !isRangeInsideEditor(editor, range)) return false;
  const point = rangeStart(range);
  return getPlaceholderRegions(editor, knownKeys).some((region) => pointWithinRegion(point, region));
}

/** Moves a visual start/end caret out of the non-editable wrapper before a browser mutation. */
export function canonicalizePlaceholderBoundaryCaret(editor: HTMLElement, range: Range): void {
  if (!range.collapsed) return;
  const point = rangeStart(range);

  getPlaceholderUnits(editor).some((unit) => {
    if (point.node === unit && point.offset === 0) {
      range.setStartBefore(unit);
      range.collapse(true);
      return true;
    }
    if (point.node === unit && point.offset === unit.childNodes.length) {
      range.setStartAfter(unit);
      range.collapse(true);
      return true;
    }

    const boundaries = unitTextBoundaries(unit);
    if (comparePoints(point, boundaries.start) === 0) {
      range.setStartBefore(unit);
      range.collapse(true);
      return true;
    }
    if (comparePoints(point, boundaries.end) === 0) {
      range.setStartAfter(unit);
      range.collapse(true);
      return true;
    }
    return false;
  });
}

/** Selects the whole atomic token adjacent to a Backspace/Delete caret. */
export function expandRangeForAdjacentPlaceholderMutation(
  editor: HTMLElement,
  range: Range,
  direction: 'backward' | 'forward',
  knownKeys: readonly string[] = [],
): HTMLElement[] {
  if (!range.collapsed || !isRangeInsideEditor(editor, range)) return [];
  const point = rangeStart(range);
  const region = getPlaceholderRegions(editor, knownKeys).find((candidate) =>
    pointsShareStructuralBoundary(editor, point, direction === 'backward' ? candidate.end : candidate.start),
  );
  if (!region) return [];

  if (region.unit) {
    range.setStartBefore(region.unit);
    range.setEndAfter(region.unit);
    return [region.unit];
  }

  range.setStart(region.start.node, region.start.offset);
  range.setEnd(region.end.node, region.end.offset);
  return [];
}

export function rangeTouchesElementContentBoundary(
  editor: HTMLElement,
  range: Range,
  element: HTMLElement,
  boundary: 'start' | 'end',
): boolean {
  if (!isRangeInsideEditor(editor, range) || !editor.contains(element)) return false;
  const elementRange = element.ownerDocument.createRange();
  elementRange.selectNodeContents(element);
  return pointsShareStructuralBoundary(
    editor,
    boundary === 'start' ? rangeStart(range) : rangeEnd(range),
    boundary === 'start' ? rangeStart(elementRange) : rangeEnd(elementRange),
  );
}

export function rangeFullyCoversElementContents(editor: HTMLElement, range: Range, element: HTMLElement): boolean {
  if (!isRangeInsideEditor(editor, range) || !editor.contains(element)) return false;
  const elementRange = element.ownerDocument.createRange();
  elementRange.selectNodeContents(element);
  const startsAtSameBoundary = rangeTouchesElementContentBoundary(editor, range, element, 'start');
  const endsAtSameBoundary = rangeTouchesElementContentBoundary(editor, range, element, 'end');
  return (
    (startsAtSameBoundary || comparePoints(rangeStart(range), rangeStart(elementRange)) < 0) &&
    (endsAtSameBoundary || comparePoints(rangeEnd(range), rangeEnd(elementRange)) > 0)
  );
}

/** Expands every touched unit to its full visible token for toolbar and shortcut formatting. */
export function expandRangeForPlaceholderFormatting(
  editor: HTMLElement,
  range: Range,
  knownKeys: readonly string[] = [],
): HTMLElement[] {
  const regions = getPlaceholderRegions(editor, knownKeys).filter((region) => overlapsRegion(range, region));
  regions.forEach((region) => {
    if (comparePoints(rangeStart(range), region.start) > 0) {
      range.setStart(region.start.node, region.start.offset);
    }
    if (comparePoints(rangeEnd(range), region.end) < 0) {
      range.setEnd(region.end.node, region.end.offset);
    }
  });
  return regions.flatMap((region) => (region.unit ? [region.unit] : []));
}

/** Expands a fully covered token from text boundaries to wrapper boundaries for cut/paste/replacement. */
export function expandRangeForPlaceholderMutation(
  editor: HTMLElement,
  range: Range,
  knownKeys: readonly string[] = [],
): HTMLElement[] {
  const regions = getPlaceholderRegions(editor, knownKeys).filter((region) => overlapsRegion(range, region));
  regions.forEach((region) => {
    const { unit } = region;
    if (!unit || !fullyCoversRegion(range, region)) return;
    const start = rangeStart(range);
    const end = rangeEnd(range);
    if (start.node === unit || unit.contains(start.node)) range.setStartBefore(unit);
    if (end.node === unit || unit.contains(end.node)) range.setEndAfter(unit);
  });
  return regions.flatMap((region) => (region.unit ? [region.unit] : []));
}
